import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * How much of the screen the takeover claims.
 *
 * - `modal` (default) — the original 85% × 85% sheet floating over the page.
 *   Right for a focused detail view you glance at and dismiss.
 * - `page` — the entire viewport, edge to edge, no rounding or border. For
 *   content that has to lay itself out (two panes, a document beside a
 *   conversation), where the 15% margin is space the content actually needed.
 *   It still behaves like a dialog — portalled, focus-trapped, closed by its
 *   own control — it just doesn't look like one.
 */
export type TakeoverVariant = 'modal' | 'page';

/**
 * Page mode has to undo a fair bit of what `DialogContent` sets up for a
 * centered sheet. Spelled out here rather than at each call site so the list
 * stays in one place:
 *   - size/position: fill the viewport from the top-left instead of centering
 *     a `max-w-lg` box, and drop the translate that centering relies on.
 *   - chrome: no border, no rounding (including the `sm:` rounding, which
 *     tailwind-merge treats as its own group and so won't drop on its own).
 *   - motion: the slide/zoom entrance is built for a small box appearing over
 *     a page. At full size it reads as the whole screen lurching, so it's
 *     neutralized down to the fade.
 */
const PAGE_CLASSES = [
  'left-0 top-0 translate-x-0 translate-y-0',
  'w-screen max-w-none h-screen max-h-none',
  'border-0 rounded-none sm:rounded-none shadow-none',
  'data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100',
  'data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0',
  'data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0',
].join(' ');

interface FullScreenTakeoverProps {
  show: boolean;
  onHide: () => void;
  noCloseButton?: boolean;
  children: React.ReactNode;
  className?: string;
  title?: string;
  /** See TakeoverVariant. Defaults to the original floating sheet. */
  variant?: TakeoverVariant;
}

export function FullScreenTakeover({
  show,
  onHide,
  noCloseButton = false,
  children,
  className,
  title,
  variant = 'modal',
}: FullScreenTakeoverProps) {
  return (
    <Dialog open={show} onOpenChange={onHide}>
      <DialogContent
        className={cn(
          'p-0 flex flex-col',
          variant === 'page'
            ? PAGE_CLASSES
            : 'max-w-[85vw] w-[85vw] h-[85vh] max-h-[85vh] translate-x-[-50%] translate-y-[-50%]',
          // Direct children only. `[&_button.absolute]` matched *any*
          // descendant, so a takeover's own content could not use an
          // absolutely-positioned button — it silently vanished. Radix's
          // close button is a direct child of DialogContent, so scoping to
          // `>` still hides the thing this is here to hide.
          (noCloseButton || title) && '[&>button]:hidden [&>button.absolute]:hidden',
          className
        )}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {title && (
          <DialogHeader className="px-6 py-4 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle>{title}</DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={onHide}
                className="h-6 w-6"
                title="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
        )}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0 h-full">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
